import { onDelegatedEvent, debounce, setLoading, formDataToObject, addParamsToUrl, processStandardBackendHeaders, replacePageContent } from '../functions.js';

/**
 * 
 * Usage:
 *    <ajax-form 
 *        data-replace-url           // Wether to replace URL or not
 *        data-target="#check-form"  // Optional, default is the <ajax-form/> itself
 *        data-trigger="submit"
 *        >
 *        <form method="post" action="url...">
 *              ... input elements
 *        </form>      
 * </ajax-form>
 *      
 * 
 * @since 2024-04-30 Remove constructor and simplify
 * @since 2024-05-08 replaceTarget is looked up in the whole document
 * @since 2024-05-08 trigger is now a data-attribute
 * @since 2024-06-26 Fix error comment
 */
export default class AjaxFormElement extends HTMLElement {
    
    _abortController = null;

    connectedCallback() {

        this.style.display = 'block';

        if (this.getAttribute('data-disable')) {
            return;
        }
        
        let form = this._getForm();

        if (!form) { 
            throw new Error('AjaxFormElement: inmediate child element must be a form');
        }

        let debouncedLaunchRequest = debounce(this.launchRequest.bind(this), 500);

        if (this.dataset.trigger === 'input') {
            onDelegatedEvent('input', this, 'input, select, textarea', debouncedLaunchRequest);
            onDelegatedEvent('sl-input', this, 'sl-switch', debouncedLaunchRequest);
        } else {
            
            this.addEventListener('submit', event => {
                event.preventDefault();
                event.stopPropagation();
                this.launchRequest(event);
            });
        }

        this._reinitAbortController();
    }

    _getReplaceTarget() {

        let replaceTarget;

        if (this.dataset.target) {
            replaceTarget = document.querySelector(this.dataset.target);
        } else {
            replaceTarget = this;
        }
        
        if (!replaceTarget.getAttribute('id')) {
            throw new Error('AjaxFormElement: target must have an id in order to be replaced');
        }

        return replaceTarget;
    }

    

    _getForm() {
        return this.querySelector('form');
    }

    _createFetchRequest(event) {
        
        let formData = new FormData(this._getForm());

        if (event.submitter && event.submitter.name) {
            formData.append(event.submitter.name, event.submitter.value);
        }

        let method = this._getMethod();

        if (method === 'get') {

            let params = formDataToObject(formData);
            delete params['_csrfToken'];

            let targetUrl = addParamsToUrl(this._getUrl(), params)

            if (this.dataset.replaceUrl) {
                history.replaceState({}, '', targetUrl);
            }
            
            return new Request(targetUrl, {
                headers: { 'X-Is-Ajax': '1' },
                signal: this._abortController.signal,
            });
            
        } else {
            return new Request(this._getUrl(), {
                method: 'POST',
                headers: { 'X-Is-Ajax': '1' },
                body: formData,
                signal: this._abortController.signal,
            });
        }
    }

    _getUrl() {
        return this._getForm().getAttribute('action') || window.location.href;
    }

    _getMethod() {
        return this._getForm().method;
    }

    launchRequest(event) {

        this._reinitAbortController();

        let replaceTarget = this._getReplaceTarget();

        setLoading(replaceTarget, true);

        if (window.NProgress) {
            NProgress.start();
        }

        let request = this._createFetchRequest(event);

        fetch(request)
            .then(res => {
                processStandardBackendHeaders(res, this);
                return res.text();
            })
            .then(res => {
                setLoading(replaceTarget, false);
                if (window.NProgress) {
                    NProgress.done();
                }

                replacePageContent(replaceTarget, res, () => {

                    // replaceTarget.scrollIntoView({
                    //     behavior: 'smooth',
                    //     block: 'start',
                    // });

                    this._dispatchEvents();
                });

            }).catch(err => {
                if (err.name === 'AbortError') {
                    return;
                }
                setLoading(replaceTarget, false);
                if (window.NProgress) {
                    NProgress.done();
                }
                console.error(err);
            });
    }

    _dispatchEvents() {
        this.dispatchEvent(new CustomEvent('ajaxform.refresh', {
            bubbles: true,
        }));

        let onRefresh = this.getAttribute('onrefresh');
        if (onRefresh !== null) {
            // https://esbuild.github.io/content-types/#direct-eval
            ((new Function(onRefresh)).bind(this))();
        }
    }

    _reinitAbortController() {
        if (this._abortController) {
            this._abortController.abort();
        }
        this._abortController = new AbortController();
    }

};

window.customElements.define('ajax-form', AjaxFormElement);